/**
 * Notifications and toasts.
 *
 * Unifies the three legacy notification systems behind one provider. The scope
 * (global / patient / doctor) is derived from the session, so a component just
 * calls `notify(...)` and the entry lands in the right bucket and the right
 * storage key.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  addNotification,
  createNotification,
  markAllRead,
  markRead,
  notificationStorageKey,
  removeNotification,
  unreadCount,
  NOTIFICATION_TYPES,
} from '../domain/notifications';
import { ROLES, useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const TOAST_DURATION_MS = 4000;

function readStored(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeStored(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* quota exceeded — notifications are non-critical, so drop silently */
  }
}

/** Which storage bucket the current session writes to. */
function scopeFor(session) {
  if (!session) return { scope: 'global', ownerId: null };
  if (session.role === ROLES.PATIENT) return { scope: 'patient', ownerId: session.patientId };
  if (session.role === ROLES.DOCTOR) return { scope: 'doctor', ownerId: session.doctorId ?? session.id };
  if (session.role === ROLES.TEAM_MEMBER) return { scope: 'team', ownerId: session.teamMemberId };
  return { scope: 'global', ownerId: null };
}

export function NotificationProvider({ children }) {
  const { session } = useAuth();
  const { scope, ownerId } = useMemo(() => scopeFor(session), [session]);
  const storageKey = useMemo(() => notificationStorageKey(scope, ownerId), [scope, ownerId]);

  const [notifications, setNotifications] = useState(() => readStored(storageKey));
  const [toasts, setToasts] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const toastTimers = useRef(new Map());

  // Swap the list when the signed-in identity changes.
  useEffect(() => {
    setNotifications(readStored(storageKey));
  }, [storageKey]);

  // Persist on every change so a reload keeps the list.
  useEffect(() => {
    writeStored(storageKey, notifications);
  }, [storageKey, notifications]);

  // Another tab writing the same key keeps this one in step — the legacy
  // `handleStorageChange`, reduced to the part that actually mattered.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === storageKey) setNotifications(readStored(storageKey));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  useEffect(
    () => () => {
      toastTimers.current.forEach((timer) => clearTimeout(timer));
      toastTimers.current.clear();
    },
    [],
  );

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = toastTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimers.current.delete(id);
    }
  }, []);

  /** Transient banner. Does not persist. */
  const toast = useCallback(
    (message, type = NOTIFICATION_TYPES.INFO, duration = TOAST_DURATION_MS) => {
      const entry = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, message, type };
      setToasts((current) => [...current, entry]);
      const timer = setTimeout(() => dismissToast(entry.id), duration);
      toastTimers.current.set(entry.id, timer);
      return entry.id;
    },
    [dismissToast],
  );

  /** Persisted notification, optionally mirrored as a toast. */
  const notify = useCallback(
    ({ type = NOTIFICATION_TYPES.INFO, title, message, action = null, icon = null, showToast = false }) => {
      const entry = createNotification({ type, title, message, action, icon });
      setNotifications((current) => addNotification(current, entry));
      if (showToast) toast(title || message, type);
      return entry;
    },
    [toast],
  );

  const value = useMemo(
    () => ({
      notifications,
      unread: unreadCount(notifications),
      panelOpen,
      setPanelOpen,
      togglePanel: () => setPanelOpen((open) => !open),

      notify,
      markAsRead: (id) => setNotifications((current) => markRead(current, id)),
      markAllAsRead: () => setNotifications((current) => markAllRead(current)),
      remove: (id) => setNotifications((current) => removeNotification(current, id)),
      clearAll: () => setNotifications([]),

      toasts,
      toast,
      success: (message) => toast(message, NOTIFICATION_TYPES.SUCCESS),
      error: (message) => toast(message, NOTIFICATION_TYPES.DANGER, 6000),
      warn: (message) => toast(message, NOTIFICATION_TYPES.WARNING),
      dismissToast,
    }),
    [notifications, panelOpen, notify, toasts, toast, dismissToast],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used inside a <NotificationProvider>');
  return context;
}
